import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ApiError } from '@/core/api';
import { usePermission } from '@/core/permissions';
import { useClasses } from '@/features/classes/queries';
import { useMembers } from '@/features/members/queries';
import type { Role } from '@/stores/session';
import { useCreateInvitation } from '../queries';
import type { CreateInvitationInput } from './../types';

type Translate = (key: string) => string;

const makeSchema = (t: Translate) =>
  z
    .object({
      email: z
        .string()
        .min(1, t('invitations:validation.emailRequired'))
        .email(t('invitations:validation.emailInvalid')),
      role: z.enum(['admin', 'teacher', 'pupil', 'parent']),
      school_class: z.string(),
      pupil: z.string(),
    })
    .superRefine((v, ctx) => {
      // Mirrors the backend matrix: pupil invites need a class, parent
      // invites need the child (invitations.services._validate_targets).
      if (v.role === 'pupil' && !v.school_class) {
        ctx.addIssue({
          code: 'custom',
          path: ['school_class'],
          message: t('invitations:validation.classRequired'),
        });
      }
      if (v.role === 'parent' && !v.pupil) {
        ctx.addIssue({
          code: 'custom',
          path: ['pupil'],
          message: t('invitations:validation.pupilRequired'),
        });
      }
    });

type FormValues = z.infer<ReturnType<typeof makeSchema>>;
const FIELD_KEYS = ['email', 'role', 'school_class', 'pupil'] as const;

/** PRD §4.3 invite matrix, from the inviter's side: only admins may invite
 *  staff (teacher/admin); admins and teachers invite pupils and parents. */
const ROLE_OPTIONS: Record<'admin' | 'teacher', Role[]> = {
  admin: ['teacher', 'pupil', 'parent', 'admin'],
  teacher: ['pupil', 'parent'],
};

interface InviteMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteMemberDialog({ open, onOpenChange }: InviteMemberDialogProps) {
  const { t } = useTranslation(['invitations', 'common']);
  const { isAdmin } = usePermission();
  const createInvitation = useCreateInvitation();

  const roleOptions = ROLE_OPTIONS[isAdmin ? 'admin' : 'teacher'];

  const schema = useMemo(() => makeSchema(t), [t]);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', role: roleOptions[0], school_class: '', pupil: '' },
  });

  const role = useWatch({ control: form.control, name: 'role' });

  const classes = useClasses(
    { is_archived: false, page_size: 50 },
    { enabled: open && role === 'pupil' }
  );
  const pupils = useMembers(
    { role: 'pupil', is_active: true, page_size: 50 },
    { enabled: open && role === 'parent' }
  );

  function close(next: boolean) {
    onOpenChange(next);
    if (!next) form.reset();
  }

  async function onSubmit(values: FormValues) {
    const input: CreateInvitationInput = {
      email: values.email,
      role: values.role,
      ...(values.role === 'pupil' ? { school_class: values.school_class } : {}),
      ...(values.role === 'parent' ? { pupil: values.pupil } : {}),
    };
    try {
      await createInvitation.mutateAsync(input);
      toast.success(t('invitations:dialog.success'));
      close(false);
    } catch (e) {
      const err = e as ApiError;
      const fe = err?.fieldErrors;
      if (fe) {
        for (const [field, messages] of Object.entries(fe)) {
          const key = FIELD_KEYS.find(k => k === field);
          if (key) form.setError(key, { message: messages.join(' ') });
        }
      }
      const general = fe?.non_field_errors;
      if (general || !fe) {
        form.setError('root', {
          message: general ? general.join(' ') : (err?.message ?? t('invitations:dialog.failed')),
        });
      }
    }
  }

  const submitting = form.formState.isSubmitting;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('invitations:dialog.title')}</DialogTitle>
          <DialogDescription>{t('invitations:dialog.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('invitations:dialog.role')}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {roleOptions.map(r => (
                        <SelectItem key={r} value={r}>
                          {t(`common:roles.${r}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('invitations:dialog.email')}</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="off"
                      placeholder={t('invitations:dialog.emailPlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {role === 'pupil' ? (
              <FormField
                control={form.control}
                name="school_class"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('invitations:dialog.class')}</FormLabel>
                    {classes.data && classes.data.results.length === 0 ? (
                      <p className="text-muted-foreground text-sm">
                        {t('invitations:dialog.noClasses')}
                      </p>
                    ) : (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={t('invitations:dialog.classPlaceholder')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {(classes.data?.results ?? []).map(cls => (
                            <SelectItem key={cls.public_id} value={cls.public_id}>
                              {cls.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
            {role === 'parent' ? (
              <FormField
                control={form.control}
                name="pupil"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('invitations:dialog.pupil')}</FormLabel>
                    {pupils.data && pupils.data.results.length === 0 ? (
                      <p className="text-muted-foreground text-sm">
                        {t('invitations:dialog.noPupils')}
                      </p>
                    ) : (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={t('invitations:dialog.pupilPlaceholder')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {(pupils.data?.results ?? []).map(pupil => (
                            <SelectItem key={pupil.public_id} value={pupil.public_id}>
                              {pupil.first_name} {pupil.last_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
            {form.formState.errors.root ? (
              <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
            ) : null}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => close(false)}>
                {t('common:cancel')}
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? t('invitations:dialog.submitting') : t('invitations:dialog.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
